import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { RegisterEntryDialog } from "@/components/RegisterEntryDialog";
import { DialogShell } from "@/components/DialogShell";
// ef-062 anchors: the two seams register check printing renders into (stubs until ef-062-6 / ef-062-7).
import { RegisterCheckBatchBar } from "@/components/RegisterCheckBatchBar";
import { RegisterCheckControls } from "@/components/RegisterCheckControls";
import { RegisterQuantityField } from "@/components/RegisterQuantityField";
import { BooksBalanceHeader } from "@/components/BooksBalanceHeader";
import { Why } from "@/components/Why";
import { ApiError, demoApi, problemMessage } from "@/api/client";
import { useReferenceData } from "@/hooks/useReferenceData";
import { useRegister } from "@/hooks/useRegister";
import { formatDateShort, formatMoney } from "@/lib/format";
import type { Account, Guid, RegisterEntry, Vendor } from "@/api/types";
import "./Books.css";
import "./BooksPeriod.css";
import "./BooksRegisterFilter.css";

/** "Locked through" wants the year spelled out — a books-period lock commonly targets a
 *  fiscal year decades out (ef-043-9's own e2e fixtures pick one on purpose, see
 *  `books-register.spec.ts`'s own remarks), where `formatDateShort`'s bare "Dec 31" would
 *  read as this year and be wrong. Kept local to this file rather than added to
 *  `lib/format.ts` — that file is outside this job's own scope. */
function formatLockDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * A failed close's own dialog error must never repeat "locked through" — the card's own
 * status line (`BooksPeriodCard`'s "Locked through …") already says that, and
 * `books-register.spec.ts`'s own "closing an already-closed fiscal year" test locates it
 * with `getByText(/locked through/i)`, which requires exactly one match on the page. The
 * API's real 409/400 `detail` text (`BooksCloseYearEndpoints.cs`) does say "…already
 * locked through this year or later" — echoed verbatim in the dialog it collides with the
 * status line (Playwright strict-mode violation, both elements visible at once). The two
 * known, tested statuses get a short, purpose-authored message instead of the raw problem
 * detail; anything else (network failure, 500) still falls back to the server's own text.
 */
/**
 * ADR 0010 §2/ef-056-3 — the register's own way to `/books/reconcile`, now that
 * Reconcile is demoted from a tab to a link (the full header-link restructure is
 * ef-056-5a's own job; this is the one link ef-056-3 owns). Real `<a>`, same
 * modified-click passthrough `Labour.tsx`'s own `handleBackClick` uses — pushed directly
 * via the History API (like `AreaTabs.go()`) rather than an `onNavigate` prop, because
 * `/books/reconcile` is its own top-level area (`App.tsx`'s `<BooksReconcile />`), not a
 * Books tab, and `Books()` takes no navigation prop today.
 */
function handleReconcileLinkClick(event: MouseEvent<HTMLAnchorElement>): void {
  handleHeaderLinkClick("/books/reconcile", event);
}

/**
 * ADR 0010 §1/D6 (ef-056-5a) — same modified-click passthrough as
 * `handleReconcileLinkClick` above, generalized so the Vendors/Recurring header links
 * (real `<a>`s, demoted from `AreaTabs`' tab strip) get SPA navigation too, rather than a
 * full page reload.
 */
function handleHeaderLinkClick(path: string, event: MouseEvent<HTMLAnchorElement>): void {
  if (
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
  ) {
    return;
  }
  event.preventDefault();
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function closeErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    return "This fiscal year is already closed. Pick a different year, or Unlock above first.";
  }
  if (err instanceof ApiError && err.status === 400) {
    return "This fiscal year has no income or expense activity to close.";
  }
  return problemMessage(err, "Could not close this year.");
}

/**
 * Books — the second tile (ADR 0009 §1). ef-042-0a gave its Activity tab the real feed:
 * every entry regardless of account kind, filterable by source/account/date. ef-042-5
 * gives the Entry tab the check register itself — one instrument account's lines, newest
 * first (ef-048-6 — `useRegister.ts` reads `order=desc`), plus the compose form that posts
 * a Check, Deposit, Transfer or split against it (`ef-042-1`/`ef-042-2`'s API). Invoices,
 * AR and the rest of the ledger ports still land later — ADR 0009's own "Books" row.
 *
 * ADR 0010 (ef-056-1) — the Entry tab's own header now carries the account label + the
 * balance headline, both of which need the register's own data BEFORE `PageHeader`
 * renders. Split into two small route components below rather than one branch inside a
 * single body, so each keeps its own hooks unconditional (never called only on one tab).
 */
// ef-056-5b (ADR 0010 §1) — the old `/books/activity` tab route is gone: `App.tsx`'s
// `LEGACY_ALIASES` now rewrites that path to `/reports/activity` before this component
// ever renders (`normalize()`/`resolvePath()`), so Books() has exactly one tab left —
// Entry — and no path branch of its own to make.
export function Books() {
  return <BooksRegisterRoute />;
}

/** 30 days back from today, UTC — the Entry tab's default register date-filter start
 *  (ef-054-0c). Same rolling-window shape `ReportsCashFlow.tsx`'s own `defaultRangeStart`
 *  uses, duplicated on purpose (that file's own reasoning: each screen's own default is
 *  free to diverge later) — this is a different screen and a different hook. */
function defaultRegisterFrom(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultRegisterTo(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The Entry tab route — ADR 0010 §1/§3. Owns reference data, the register-account
 * selection, and the register page's own read (`useRegister`) all at THIS level, one
 * level above the register card itself, so `PageHeader`'s title (the account label) and
 * `action` slot (the balance headline, `BooksBalanceHeader`) can be computed from the
 * SAME state the card renders from — never a second, drifting copy of it
 * (`persona`'s own "single source of truth" rule). `BooksRegisterPanel` below is now
 * presentational: every value it needs arrives as a prop.
 */
function BooksRegisterRoute() {
  const { vendors, accounts, entityId, status: refStatus, error: refError } = useReferenceData();
  const registerFieldId = useId();

  const assetAccounts = useMemo(() => accounts.filter((a) => a.kind === "Asset"), [accounts]);
  // Every other account — the Check/Deposit category picker and each Split line's own
  // picker (`RegisterEntryDialog`'s own remarks: Transfer, not Simple, is what an Asset
  // destination is for).
  const categoryAccounts = useMemo(() => accounts.filter((a) => a.kind !== "Asset"), [accounts]);

  const [registerAccountId, setRegisterAccountId] = useState("");
  // Defaults to the first (lowest-numbered) instrument account once reference data
  // loads — GetAccountsAsync orders by `number`, so "1010 Operating Checking" sorts
  // ahead of "1020 Savings" — without ever overwriting a farmer's own later pick.
  useEffect(() => {
    if (registerAccountId === "" && assetAccounts.length > 0) {
      setRegisterAccountId(assetAccounts[0]!.id);
    }
  }, [assetAccounts, registerAccountId]);

  const transferAccounts = useMemo(
    () => assetAccounts.filter((a) => a.id !== registerAccountId),
    [assetAccounts, registerAccountId],
  );

  // Date filter (ef-054-0c) — `registerFrom`/`registerTo` are the draft inputs, applied
  // only on submit. `appliedFrom`/`appliedTo` are what actually reaches `useRegister`,
  // and default to the same last-30-days window so the register is already scoped on
  // first load, no Apply click required. Neither resets when `registerAccountId`
  // changes — switching accounts re-applies the CURRENT filter, not the default.
  const registerFromId = useId();
  const registerToId = useId();
  const [registerFrom, setRegisterFrom] = useState(defaultRegisterFrom);
  const [registerTo, setRegisterTo] = useState(defaultRegisterTo);
  const [appliedFrom, setAppliedFrom] = useState(defaultRegisterFrom);
  const [appliedTo, setAppliedTo] = useState(defaultRegisterTo);

  const registerRangeIsBackwards = registerFrom !== "" && registerTo !== "" && registerTo < registerFrom;
  const canApplyRegisterFilter = registerFrom !== "" && registerTo !== "" && !registerRangeIsBackwards;

  function applyRegisterFilter(): void {
    if (!canApplyRegisterFilter) return;
    setAppliedFrom(registerFrom);
    setAppliedTo(registerTo);
  }

  const { entries, status, error, hasMore, loadMore, refresh } = useRegister(
    registerAccountId || null,
    appliedFrom,
    appliedTo,
  );

  const selectedAccount = assetAccounts.find((a) => a.id === registerAccountId) ?? null;
  // ADR 0010 §1 — the header's own h1 IS the account label (mock line 35's
  // "Checking · 1010 Operating" shape) once one resolves; "Books" is the loading/no-
  // instrument-accounts-yet fallback, never a blank heading.
  const headerTitle = selectedAccount ? `${selectedAccount.number} — ${selectedAccount.name}` : "Books";

  // ADR 0010 §3 — computed client-side from the already-loaded register page, no new
  // read route. `entries` is newest-first (`useRegister`'s own `order: desc`), so the
  // first row's own `runningBalance` IS the account's balance through today.
  // `entry.cleared` is read defensively (`?? false`) against the pre-ef-056-3 wire shape,
  // where the field may be `undefined` at runtime despite the type saying `boolean`
  // (ef-056-0a anchor; real per-row values land ef-056-3) — an absent flag reads as "not
  // yet cleared", never as cleared.
  const balance = entries[0]?.runningBalance ?? 0;
  const clearedBalance = entries.reduce((sum, e) => (e.cleared ?? false ? sum + e.amount : sum), 0);
  const outstandingCount = entries.filter((e) => !(e.cleared ?? false)).length;

  return (
    <main className="books-page">
      <PageHeader
        title={headerTitle}
        subtitle="The check register — post a Check, Deposit, Transfer or split, and see it land."
        action={
          <div className="books-page__header-actions">
            <BooksBalanceHeader balance={balance} clearedBalance={clearedBalance} outstandingCount={outstandingCount} />
            {/* ADR 0010 §1/D6 — Vendors/Recurring demoted from tabs to plain links, living
                in the header's action slot alongside the balance headline (`ef-056-5a-build.md`). */}
            <nav className="books-page__header-links" aria-label="Books">
              <a href="/books/vendors" onClick={(e) => handleHeaderLinkClick("/books/vendors", e)}>
                Vendors
              </a>
              <a href="/books/recurring" onClick={(e) => handleHeaderLinkClick("/books/recurring", e)}>
                Recurring
              </a>
            </nav>
          </div>
        }
      />
      <BooksRegisterPanel
        registerFieldId={registerFieldId}
        entityId={entityId}
        refStatus={refStatus}
        refError={refError}
        assetAccounts={assetAccounts}
        categoryAccounts={categoryAccounts}
        transferAccounts={transferAccounts}
        vendors={vendors}
        registerAccountId={registerAccountId}
        setRegisterAccountId={setRegisterAccountId}
        registerFromId={registerFromId}
        registerToId={registerToId}
        registerFrom={registerFrom}
        setRegisterFrom={setRegisterFrom}
        registerTo={registerTo}
        setRegisterTo={setRegisterTo}
        registerRangeIsBackwards={registerRangeIsBackwards}
        canApplyRegisterFilter={canApplyRegisterFilter}
        onApplyRegisterFilter={applyRegisterFilter}
        entries={entries}
        status={status}
        error={error}
        hasMore={hasMore}
        loadMore={loadMore}
        refresh={refresh}
      />
    </main>
  );
}

interface BooksRegisterPanelProps {
  registerFieldId: string;
  entityId: Guid | null;
  refStatus: "loading" | "ready" | "error";
  refError: string | null;
  assetAccounts: Account[];
  categoryAccounts: Account[];
  transferAccounts: Account[];
  vendors: Vendor[];
  registerAccountId: string;
  setRegisterAccountId: (id: string) => void;
  registerFromId: string;
  registerToId: string;
  registerFrom: string;
  setRegisterFrom: (value: string) => void;
  registerTo: string;
  setRegisterTo: (value: string) => void;
  registerRangeIsBackwards: boolean;
  canApplyRegisterFilter: boolean;
  onApplyRegisterFilter: () => void;
  entries: RegisterEntry[];
  status: "loading" | "ready" | "error";
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * The register card — ADR 0010 §1. One card, one ruled list, the compose row typed in
 * place as the list's own first item (no separate account-picker card, no separate
 * compose card). Purely presentational: `BooksRegisterRoute` above owns every piece of
 * state this needs.
 */
function BooksRegisterPanel({
  registerFieldId,
  entityId,
  refStatus,
  refError,
  assetAccounts,
  categoryAccounts,
  transferAccounts,
  vendors,
  registerAccountId,
  setRegisterAccountId,
  registerFromId,
  registerToId,
  registerFrom,
  setRegisterFrom,
  registerTo,
  setRegisterTo,
  registerRangeIsBackwards,
  canApplyRegisterFilter,
  onApplyRegisterFilter,
  entries,
  status,
  error,
  hasMore,
  loadMore,
  refresh,
}: BooksRegisterPanelProps) {
  // ef-043-9 — the books-period lock, entity-scoped (D4: one cutoff date per entity, not
  // per fiscal year). Fetched on load so a reload shows the real status
  // (`ef-043-9-build.md`'s own instruction), and again whenever `entityId` resolves.
  const [lockedThrough, setLockedThrough] = useState<string | null>(null);
  const [lockStatus, setLockStatus] = useState<"loading" | "ready" | "error">("loading");
  const [lockError, setLockError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) return;
    let cancelled = false;
    const controller = new AbortController();
    setLockStatus("loading");
    setLockError(null);
    demoApi
      .getYearLock(entityId, controller.signal)
      .then((result) => {
        if (cancelled) return;
        setLockedThrough(result.lockedThrough);
        setLockStatus("ready");
      })
      .catch((err) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setLockStatus("error");
        setLockError(problemMessage(err, "Could not load the books period."));
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [entityId]);

  // Derived, not synced (`persona`'s own "Derived state" rule) — whether the composer
  // below is usable follows directly from `lockedThrough`, never a second flag that could
  // drift from it.
  const periodLocked = lockedThrough !== null;

  // ef-056-5a (ADR 0010 §1) — the account switch is no longer a form control that's
  // always on screen (`books-register.spec.ts`'s own header comment); it's disclosed
  // behind a "Change account" button. Local UI-only state — never lifted, never synced
  // with `registerAccountId` itself.
  const [showAccountPicker, setShowAccountPicker] = useState(false);

  return (
    <section
      className="books-register"
      aria-labelledby="books-register-heading"
      // Ground truth for a tester, rules/ui-breadcrumbs.md §1 — read off the DOM rather
      // than inferred from sentence text.
      data-state={status === "ready" && entries.length === 0 ? "empty" : status}
    >
      <h2 id="books-register-heading" className="books-register__heading">
        Entry
      </h2>

      <p className="books-register__account-switch">
        <button
          type="button"
          className="btn btn--ghost"
          aria-expanded={showAccountPicker}
          aria-controls={registerFieldId}
          onClick={() => setShowAccountPicker((shown) => !shown)}
        >
          Change account
        </button>
      </p>

      {showAccountPicker && (
      <div className="books-register__account-picker">
        <label htmlFor={registerFieldId}>Register account</label>
        <select
          id={registerFieldId}
          value={registerAccountId}
          onChange={(e) => setRegisterAccountId(e.target.value)}
          disabled={refStatus === "loading" || assetAccounts.length === 0}
        >
          {assetAccounts.length === 0 && <option value="">No instrument accounts yet</option>}
          {assetAccounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.number} — {a.name}
            </option>
          ))}
        </select>
      </div>
      )}

      {/* ADR 0010 §2 — the CSV/candidate reconcile flow, demoted to a link; the row-level
          cleared checkbox below is the lightweight, independent path for the common case. */}
      <p className="books-register__reconcile-link">
        <a href="/books/reconcile" onClick={handleReconcileLinkClick}>
          Match to bank statement
        </a>
      </p>

      {/* A plain div, not a <form> — ADR 0010 §5 budgets the WHOLE page at exactly one
          <form> (the compose row below), so this filter applies on button click rather
          than form submit. Same className/CSS as before (BooksRegisterFilter.css is
          tag-agnostic — see its own selectors). */}
      <div className="books-register__filter">
        <div className="books-register__filter-field">
          <label htmlFor={registerFromId}>From</label>
          <input
            id={registerFromId}
            type="date"
            value={registerFrom}
            onChange={(e) => setRegisterFrom(e.target.value)}
          />
        </div>
        <div className="books-register__filter-field">
          <label htmlFor={registerToId}>To</label>
          <input
            id={registerToId}
            type="date"
            value={registerTo}
            aria-invalid={registerRangeIsBackwards || undefined}
            onChange={(e) => setRegisterTo(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn--secondary books-register__filter-apply"
          disabled={!canApplyRegisterFilter}
          onClick={onApplyRegisterFilter}
        >
          Apply
        </button>
      </div>

      {registerRangeIsBackwards && (
        <p className="books-register__filter-error" role="alert">
          The end of the range cannot be before its start.
        </p>
      )}

      {refStatus === "error" && (
        <StateMessage tone="error" title="Could not load accounts." detail={refError ?? undefined} />
      )}

      {entityId && (
        <BooksPeriodCard
          entityId={entityId}
          lockedThrough={lockedThrough}
          status={lockStatus}
          error={lockError}
          onLockChange={setLockedThrough}
        />
      )}

      {periodLocked && (
        <p className="books-period__composer-note" role="status">
          This period is closed. Unlock the books period above to add entries.
        </p>
      )}

      {/* ADR 0010 §1/§5 — ONE ruled list. The compose row is the list's own first item
          (row zero), not a separate card above it; the register's real rows follow,
          newest first. Loading/error/empty states render as the list's own row too, so
          the list is never absent while the composer above it still needs somewhere to
          live. */}
      {/* ef-062 anchor - "Print unprinted checks" (ef-062-7 REPLACES the component; this line stays). */}
      {registerAccountId && <RegisterCheckBatchBar registerAccountId={registerAccountId} refresh={refresh} />}

      <ul className="books-register__list ruled-rows" aria-label="Register">
        {registerAccountId && (
          // ef-056-5a fix — `RegisterEntryDialog` already returns its own `<li
          // className="register-entry">` (ef-056-2's reshape, see that component's own
          // `disabled` remarks); wrapping it in a SECOND `<li><fieldset>` here nested the
          // real `<li>` inside a `<fieldset>` instead of directly under this `<ul>`, which
          // is what axe's "list item must be contained in a list" violation was catching.
          // `disabled` passes straight through instead.
          <RegisterEntryDialog
            registerAccountId={registerAccountId}
            categoryAccounts={categoryAccounts}
            transferAccounts={transferAccounts}
            vendors={vendors}
            onPosted={() => void refresh()}
            disabled={periodLocked}
          />
        )}

        {status === "loading" && (
          <li className="books-register__state-row">
            <StateMessage tone="loading" title="Loading the register…" />
          </li>
        )}

        {status === "error" && (
          <li className="books-register__state-row">
            <StateMessage tone="error" title="Could not load the register." detail={error ?? undefined} />
          </li>
        )}

        {status === "ready" && entries.length === 0 && (
          <li className="books-register__state-row">
            <StateMessage
              tone="empty"
              title="Nothing posted to this account yet."
              detail="Entries you post above will show up here, newest first."
            />
          </li>
        )}

        {status === "ready" && entries.map((entry) => <RegisterRow key={entry.id} entry={entry} refresh={refresh} />)}
      </ul>

      {hasMore && (
        <button type="button" className="btn btn--ghost books-register__load-more" onClick={() => void loadMore()}>
          Load more
        </button>
      )}
    </section>
  );
}

function RegisterRow({ entry, refresh }: { entry: RegisterEntry; refresh: () => Promise<void> }) {
  // `RegisterEntryResponse.Type` — "Deposit"/"Check"/"Transfer"/"Mixed"/"Uncategorized"
  // (`BooksRegisterRepository.ClassifyType`) — lowercased for `data-state` so a tester
  // (and this row's own CSS) reads ground truth off the DOM rather than the sentence.
  // Legacy colour convention (`.reeves/context/legacy-entry-colours.md`): Check/Deposit/
  // Transfer each got their own ledger-row tint in the desktop app; Mixed/Uncategorized
  // map to the same "something's wrong with this row" red the legacy Red fallback used.
  // The COLOUR is reinforcement only — the type name and (below) the plain-text integrity
  // note are what actually say so.
  const state = entry.type.toLowerCase();

  // ADR 0010 §2/ef-056-3 — local, optimistic mirror of `entry.cleared`. Resynced whenever
  // the prop itself moves (a reload, or `refresh()` after a later re-fetch) — same
  // "the prop is truth outside an in-flight edit" shape a controlled checkbox needs.
  const [cleared, setCleared] = useState(entry.cleared);
  const [clearedPending, setClearedPending] = useState(false);
  const [clearedError, setClearedError] = useState<string | null>(null);

  useEffect(() => {
    setCleared(entry.cleared);
  }, [entry.cleared]);

  async function handleClearedChange(next: boolean): Promise<void> {
    const previous = cleared;
    setCleared(next);
    setClearedPending(true);
    setClearedError(null);
    try {
      // ADR 0010 §2/ef-056-3-LEAD anchor — `POST /register/{entryLineId}/cleared`'s own
      // path param is `entry_line.id`, never `entry.id` (the two differ on every split);
      // `entry.entryLineId` is the field the Spec Architect amendment added so this call
      // targets the account's own line, not the entry.
      await demoApi.setEntryLineCleared(entry.entryLineId, next);
      // ef-056 holdback #2 — the parent's own `entries` (source of `clearedBalance`/
      // `outstandingCount` in `BooksBalanceHeader`, ADR 0010 §1) is never re-fetched by
      // this row's local, optimistic `cleared` mirror above. Re-run the SAME `refresh()`
      // `RegisterEntryDialog`'s own `onPosted` already uses, so the headline moves off
      // its stale figure the instant the POST succeeds — never on failure, where
      // `previous` below is restored instead.
      void refresh();
    } catch (err) {
      setCleared(previous);
      setClearedError(problemMessage(err, "Could not update cleared status."));
    } finally {
      setClearedPending(false);
    }
  }

  return (
    <li className="books-register__row" data-entry-id={entry.id} data-state={state}>
      <label className="books-register__cleared">
        <input
          type="checkbox"
          aria-label="Cleared"
          checked={cleared}
          disabled={clearedPending}
          data-entry-line-id={entry.entryLineId}
          onChange={(e) => void handleClearedChange(e.target.checked)}
        />
      </label>
      <time className="books-register__date" dateTime={entry.occurredOn}>
        {formatDateShort(entry.occurredOn)}
      </time>
      <span className="books-register__meta">
        <span className="books-register__type">{entry.type}</span>
        <span className="books-register__other">{entry.otherAccountName ?? "No other leg"}</span>
        {entry.memo && <span className="books-register__memo">{entry.memo}</span>}
        {entry.red && (
          <span className="books-register__flag">
            Needs review — this entry can’t be taken at face value.
          </span>
        )}
        {clearedError && (
          <span className="books-register__flag" role="alert">
            {clearedError}
          </span>
        )}
        {/* ef-062 anchor - the per-payment check controls (ef-062-6 REPLACES the component; this line stays). */}
        <RegisterCheckControls entry={entry} refresh={refresh} />
        {/* ef-064-8 anchor - the "how many" of this entry (ef-064-8 REPLACES the component; this line stays). */}
        <RegisterQuantityField entry={entry} refresh={refresh} />
      </span>
      <span className={`books-register__amount books-register__amount--${entry.amount < 0 ? "neg" : "pos"}`}>
        {/* ADR 0010 §3/§5 — a visually-hidden column label, same node as the figure. Two
            reasons: (1) neither column has a visible header once the account-picker card
            collapsed away, so a screen reader would otherwise hear a bare dollar amount
            with no context; (2) it keeps this row's own figure out of a lone
            `$X,XXX.XX` text match, which is what `BooksBalanceHeader`'s headline figure
            (`books-register.spec.ts`'s "balance headline" block) locates by — without
            this, a page with any Deposit row would strict-mode-collide with the
            headline's own `getByText`. */}
        <span className="visually-hidden">{entry.amount < 0 ? "Payment " : "Deposit "}</span>
        {formatMoney(entry.amount)}
      </span>
      <span className="books-register__running-total">
        <span className="visually-hidden">Balance </span>
        {formatMoney(entry.runningBalance)}
      </span>
    </li>
  );
}

/**
 * ef-043-9 — the "Books period" card, `BooksRegisterPanel`'s own Entry tab (there is no
 * separate `BooksRegister.tsx` — see the build brief's own Correction 1). Shows the
 * entity's current lock (`GET /close-year/status`, ef-043-8), and the owner-only actions
 * that change it: `Close year` (permanent — zeroes income/expense into Retained Earnings
 * and locks entries through year-end) and `Unlock` (lifts the lock; never reverses the
 * closing entry itself — `BooksCloseYearRepository`'s own remarks). Both actions confirm
 * through a real `role="alertdialog"`, never `window.confirm` (blocks Playwright).
 */
function BooksPeriodCard({
  entityId,
  lockedThrough,
  status,
  error,
  onLockChange,
}: {
  entityId: Guid;
  lockedThrough: string | null;
  status: "loading" | "ready" | "error";
  error: string | null;
  onLockChange: (lockedThrough: string | null) => void;
}) {
  const headingId = useId();
  const fiscalYearId = useId();
  const closeTitleId = useId();
  const closeDescId = useId();
  const unlockTitleId = useId();
  const unlockDescId = useId();

  const [fiscalYear, setFiscalYear] = useState("");
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const [confirmingUnlock, setConfirmingUnlock] = useState(false);
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);

  const closeTriggerRef = useRef<HTMLButtonElement>(null);
  const unlockTriggerRef = useRef<HTMLButtonElement>(null);

  const locked = lockedThrough !== null;
  // A plain 4-digit year — `CloseYearRequest.FiscalYear` (`BooksCloseYearContracts.cs`)
  // takes an int, and a partially typed value must not enable the button that fires the
  // permanent action below.
  const trimmedFiscalYear = fiscalYear.trim();
  const parsedFiscalYear = /^\d{4}$/.test(trimmedFiscalYear) ? Number(trimmedFiscalYear) : null;

  function openCloseConfirm(): void {
    setCloseError(null);
    setConfirmingClose(true);
  }

  function closeCloseConfirm(): void {
    setConfirmingClose(false);
    setCloseError(null);
    closeTriggerRef.current?.focus();
  }

  async function confirmClose(): Promise<void> {
    if (parsedFiscalYear === null) return;
    setCloseBusy(true);
    setCloseError(null);
    try {
      const result = await demoApi.closeYear(entityId, parsedFiscalYear);
      onLockChange(result.lockedThrough);
      setConfirmingClose(false);
      setFiscalYear("");
      closeTriggerRef.current?.focus();
    } catch (err) {
      // A 409 (already closed) or 400 (nothing to close) — surfaced in the dialog rather
      // than thrown, so a retry (a different year) is one click away, and the lock line
      // above stays exactly what it was (`onLockChange` is never called on failure).
      setCloseError(closeErrorMessage(err));
    } finally {
      setCloseBusy(false);
    }
  }

  function openUnlockConfirm(): void {
    setUnlockError(null);
    setConfirmingUnlock(true);
  }

  function closeUnlockConfirm(): void {
    setConfirmingUnlock(false);
    setUnlockError(null);
    unlockTriggerRef.current?.focus();
  }

  async function confirmUnlock(): Promise<void> {
    setUnlockBusy(true);
    setUnlockError(null);
    try {
      await demoApi.unlockYear(entityId);
      unlockSucceeded();
    } catch (err) {
      // `unlockYear` answers 200 with an empty body (never 204) — `client.ts`'s shared
      // `request()` helper still calls `response.json()` on every non-204 response, which
      // throws a native `SyntaxError` on that empty body even though the unlock itself
      // reached the server and succeeded. A real failure (4xx/5xx, timeout, offline) is
      // always wrapped as `ApiError` by that same helper, so anything that is NOT an
      // `ApiError` here is exactly that empty-body-on-success case, not a genuine error —
      // out of scope to fix `client.ts`/the API from this card (`ef-043-9-build.md`), so
      // the workaround lives here instead of surfacing a false failure to the owner.
      if (!(err instanceof ApiError)) {
        unlockSucceeded();
        return;
      }
      setUnlockError(problemMessage(err, "Could not unlock this period."));
    } finally {
      setUnlockBusy(false);
    }
  }

  function unlockSucceeded(): void {
    onLockChange(null);
    setConfirmingUnlock(false);
    // The Unlock trigger itself unmounts once the period reopens (rendered only while
    // `locked`) — the `Close year` trigger is the next reachable control on the card, so
    // focus lands there instead of silently falling back to <body>.
    closeTriggerRef.current?.focus();
  }

  return (
    <section className="books-period" aria-labelledby={headingId} data-state={locked ? "locked" : "open"}>
      <h3 id={headingId} className="books-period__heading">
        Books period
      </h3>

      {status === "loading" && <p className="books-period__status">Checking the books period…</p>}

      {status === "error" && (
        <p className="books-period__status books-period__status--error" role="alert">
          {error ?? "Could not load the books period."}
        </p>
      )}

      {status === "ready" && (
        <p className="books-period__status">
          {locked ? `Locked through ${formatLockDate(lockedThrough!)}` : "Open — no year closed yet."}
        </p>
      )}

      <Why>
        Closing a year zeroes every income and expense account for it into 3900 Retained
        Earnings and locks entries through December 31 of that year. The closing entry
        itself always stays on the books — Unlock only lifts the lock, it never reverses
        what closing posted.
      </Why>

      <div className="books-period__form">
        <div className="books-period__field">
          <label htmlFor={fiscalYearId}>Fiscal year</label>
          <input
            id={fiscalYearId}
            type="number"
            inputMode="numeric"
            step="1"
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
          />
        </div>
        <button
          ref={closeTriggerRef}
          type="button"
          className="btn btn--primary"
          disabled={parsedFiscalYear === null}
          onClick={openCloseConfirm}
        >
          Close year
        </button>
        {locked && (
          <button ref={unlockTriggerRef} type="button" className="btn btn--ghost" onClick={openUnlockConfirm}>
            Unlock
          </button>
        )}
      </div>

      {confirmingClose && (
        <PeriodConfirmDialog
          titleId={closeTitleId}
          descId={closeDescId}
          title="Close this fiscal year?"
          description={
            <>
              Closing zeroes every income and expense account into Retained Earnings and locks
              entries through December 31, {parsedFiscalYear ?? trimmedFiscalYear}. This cannot
              be undone by editing — only Unlock lifts the lock, and the closing entry itself
              stays on the books.
            </>
          }
          confirmLabel="Confirm close year"
          confirmBusyLabel="Closing…"
          confirmTone="danger"
          busy={closeBusy}
          error={closeError}
          onCancel={closeCloseConfirm}
          onConfirm={() => void confirmClose()}
        />
      )}

      {confirmingUnlock && (
        <PeriodConfirmDialog
          titleId={unlockTitleId}
          descId={unlockDescId}
          title="Unlock the books period?"
          description="This reopens every account for new entries. The closing entry itself is never reversed — unlock only lifts the lock."
          confirmLabel="Confirm unlock"
          confirmBusyLabel="Unlocking…"
          confirmTone="primary"
          busy={unlockBusy}
          error={unlockError}
          onCancel={closeUnlockConfirm}
          onConfirm={() => void confirmUnlock()}
        />
      )}
    </section>
  );
}

/**
 * The two-button confirm shared by both `BooksPeriodCard` actions — same
 * overlay/alertdialog/focus-trap shape `BooksVendors.tsx`'s own delete confirm uses
 * (real `role="alertdialog"`, Escape-to-cancel, a two-stop Tab trap), factored out here
 * rather than duplicated because Close and Unlock differ only in copy and which endpoint
 * fires.
 */
function PeriodConfirmDialog({
  titleId,
  descId,
  title,
  description,
  confirmLabel,
  confirmBusyLabel,
  confirmTone,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  titleId: string;
  descId: string;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  confirmBusyLabel: string;
  confirmTone: "primary" | "danger";
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <DialogShell
      title={title}
      titleId={titleId}
      role="alertdialog"
      size="sm"
      onClose={onCancel}
      busy={busy}
      initialFocus={cancelBtnRef}
      describedBy={descId}
      footer={
        <>
          <button ref={cancelBtnRef} type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className={`btn btn--${confirmTone}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? confirmBusyLabel : confirmLabel}
          </button>
        </>
      }
    >
      <p id={descId} className="books-period__dialog-desc">
        {description}
      </p>
      {error && (
        <p className="books-period__dialog-error" role="alert">
          {error}
        </p>
      )}
    </DialogShell>
  );
}
